import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Box, CircularProgress, IconButton, Link, Stack, Tooltip, Typography } from '@mui/material'
import { ChevronLeftIcon, ChevronRightIcon, PanelRightCloseIcon, PanelRightOpenIcon } from 'lucide-react'
import { Button, Card, Input, Skeleton } from '@repo/ui'

import { api } from 'services'
import { ASK_PANEL_SLIDE_DURATION_MS, ASK_PANEL_SLIDE_EASE } from 'ask'
import { AppearanceToggle, ProductSelectField, StatusChip } from 'components'
import { useSnackbar } from 'contexts'
import { useColorMode, useUserProfileQuery } from 'hooks'
import { useCompaniesQuery } from 'hooks/api'
import { listedCompanies } from 'utils/listedCompany'
import { ROUTES } from 'routes'

import { LabsSideColumn, LabsSideColumnSkeleton } from '../components/LabsSideColumn'
import {
  BRAND_CONTEXT_CARD_H,
  BrandContext,
  BrandContextCard,
  BrandContextCompany,
} from '../components/BrandContextCard'

type EmailGroup = 'app' | 'platform' | 'shared'

/** Migration status badge — uses the shared StatusChip (parity with the Components lab). */
const TEMPLATE_STATUSES = {
  live: { label: 'Live', color: '#16a34a', backgroundColor: 'rgba(22,163,74,0.1)', borderColor: '#86efac' },
  draft: { label: 'Draft', color: '#f59e0b', backgroundColor: 'rgba(245,158,11,0.1)', borderColor: '#fcd34d' },
}

interface EmailTemplate {
  id: string
  name: string
  subject: string
  group: EmailGroup
  category: string
  status: 'live' | 'draft'
  sampleProps: Record<string, any>
  /** VRG-512 — whether `id` also has an SMS body in SMS_TEMPLATE_BODY_MAP. */
  hasSms: boolean
}

/** A `shared` template (e.g. email verification) shows under both tabs. */
const inTab = (t: EmailTemplate, tab: 'app' | 'platform') => t.group === tab || t.group === 'shared'

/**
 * The registry's two halves, as the left column's scope filter (VRG-560).
 *
 * These were a `Tabs` row in a sticky band above the whole section. They are a FILTER on
 * the index, not page navigation, so they now head the column that holds that index — see
 * `LabsSideColumn` for the full argument and for what deleting the band bought.
 */
const SCOPE_OPTIONS = [
  { label: 'App', value: 'app' },
  { label: 'Platform', value: 'platform' },
]

/**
 * ── Column geometry ────────────────────────────────────────────────────────
 *
 * ⚠️ MEASURED at 1280x800 on `/internal/labs/notifications`.
 *
 * This section renders inside chrome it does not own: the platform sidebar (288px
 * expanded, 80px collapsed) and then the Labs page's own layout. Before VRG-560 the Labs
 * section nav was a `md={2}` COLUMN, so this section was two levels deep and had 935px to
 * fit 1112px of content in. Turning that nav into a horizontal tab row returned its
 * ~224px (the column plus its 24px gutter) to this section.
 *
 * Budget: `LABS_TOC_W + gap + preview + gap + PROPS_W`, so the preview column is
 * `usable − (288 + 32 + 32 + 312)` = `usable − 664`.
 *
 * | sidebar   | usable here | preview, panel open | scale | panel hidden | scale |
 * |-----------|-------------|---------------------|-------|--------------|-------|
 * | collapsed | 1151        | 487                 | 81%   | 831          | 100%  |
 * | expanded  |  943        | 279                 | 46%   | 623          | 100%  |
 *
 * Every cell above is MEASURED at 1280x800, not derived from the budget line. The scale
 * column is computed from the frame's `clientWidth`, which is 2px under the column width
 * because it excludes the frame's 1px border on each side — 485, 277, 829 and 621
 * respectively. That is deliberate (see the width measurement's own note) and it is why
 * the expanded row scales at 46% rather than the 47% the column arithmetic alone predicts.
 *
 * **The email no longer has to fit — it SCALES to fit** (see the preview frame). The card
 * keeps its true 600px coordinate space and is `transform: scale()`d down to the column,
 * so a 303px column shows the whole email at ~51% rather than half the email at 100%. The
 * arithmetic above is therefore about how big the email RENDERS, not whether it is
 * complete, which is what made widening the two rails affordable at all.
 *
 * ## The gaps are paid for out of the preview, and the expanded row is the price
 *
 * VRG-560 took the rails from 256/280 at a 12px gap to 288/312, and the gap from 12 to 20
 * and then to 32 — 136px in total, all of it out of the middle column. Stated rather than
 * buried, because the two rows above do not degrade equally:
 *
 * - **Collapsed (81%)** is the case that matters. It is the resting state: the sidebar
 *   ships in `auto`, which is collapsed until hovered, so this is the row a reader is
 *   actually looking at while reviewing templates. The last 20 -> 32 step cost it four
 *   points of scale (85% -> 81%) and bought 12px of air on each side of the artifact,
 *   which was the explicit ask.
 * - **Expanded + panel open (46%)** is genuinely poor, and it was ALREADY poor at 64%
 *   before any of this. 943px cannot hold a 600px artifact between two rails at any of
 *   these widths; that combination is over-constrained on its own terms, not by the
 *   gaps. **"Hide panel" is the answer, and it clamps to a true 100% in BOTH sidebar
 *   states** (831 and 623, each ≥ EMAIL_W) — which matters more than it used to, because
 *   it is the only route back to 1:1 since the "Fit width" toggle was removed.
 *
 * If the expanded-sidebar row needs to be reviewable at a real size, the lever is the
 * panel toggle, not another 30px shaved off a rail.
 *
 * That is also why the old `minWidth: EMAIL_W`-plus-horizontal-scroll treatment is gone.
 * It was the right answer while the only two options were "clip silently" and "scroll
 * visibly"; scaling is strictly better than both.
 */
/**
 * The index column's width now lives in `../constants` as `LABS_TOC_W`, because
 * `ComponentsLab` renders the same column (`LabsSideColumn`) and a second copy of the
 * number is a second thing to drift. Its derivation — the 219.38px longest template name,
 * the rail's 30px of inset, the 250px floor §49 sets — moved with it.
 */
/**
 * Props/SMS rail width — 312, up from 280 (VRG-560).
 *
 * Unlike `LABS_TOC_W` this is NOT forced by a measurement, and saying so is the point: at 280
 * the longest label already fitted. The card is 280 outer, less a 1px border each side and
 * 24px of padding each side, so **230px of content** — measured, and confirmed by the
 * rendered `Input` reporting exactly 230. The longest props label across the registry's
 * 136 flattened dot-paths is **`customer.company.logoTopImgLink` at 214.8px** in the
 * label's own 14px/400 Lato, so 280 cleared it by 15.2px.
 *
 * 312 is therefore a COMFORT width, granted on the user's "way too tight" note rather than
 * derived from a wrap. It takes the content column to 262 and the slack to 47.2px, which
 * is the same "one longer path does not immediately break it" margin `LABS_TOC_W` now carries.
 */
const PROPS_W = 312
/**
 * The email card's own width — `width: 600px; max-width: 100%` in `base-email.tsx`.
 *
 * This is the card's COORDINATE SPACE, and it is deliberately not negotiable: the iframe
 * renders at exactly this width whatever the column measures, and the scale transform
 * changes only how large that 600px-wide artifact is drawn. Letting the email reflow
 * narrower would change what is being reviewed, which defeats the surface's whole purpose.
 */
const EMAIL_W = 600
/**
 * Gap between columns, `spacing={8}` = 32px on the `@repo/ui` 4px base (§29). Was 5 (20px),
 * and 3 (12px) before that.
 *
 * ## Why the ORIGINAL reason for 12 no longer applies
 *
 * 12 was chosen to keep the preview column above 600: measured at 1280x800 with the
 * sidebar collapsed the row is 1151px, so at a 16px gap the preview came out at **599** —
 * one pixel under the email card's 600, enough to put the frame into horizontal scroll for
 * no reason a reader could see. 12px landed it at 607, just over the line.
 *
 * That constraint died with the "Fit width" toggle (VRG-560). The preview now ALWAYS
 * scales to its column, so falling under 600 costs a few percent of rendered size rather
 * than a scrollbar, and there is no cliff at 600 to defend by a single pixel. The gap is
 * free to be chosen for how it reads instead of for what it clears.
 *
 * ## Why 32, and what it costs
 *
 * This is a middle column holding a single artifact between two rails, and the ask was for
 * it to breathe. Because it is the MIDDLE column, the stack's own spacing is the whole
 * lever: one number puts equal air on both sides of the email, which is what "margin
 * either side of the preview" means. There is no separate padding to reach for.
 *
 * §29 fixes 12px for a BUTTON GROUP — controls acting as one unit, where proximity is the
 * thing being communicated. These are three independent regions (an index, the artifact
 * under review, its inputs) and the gap's job is the opposite: to say they are separate.
 * 16 is the step the panel already spends BETWEEN ITS OWN TWO CARDS, so a column boundary
 * at 16 would weigh the same as a card boundary inside one of them; 20 cleared that but
 * still read tight against a 600px artifact.
 *
 * 32 is the page-level step, and that is the right register for the outermost boundary on
 * the surface. It is NOT free: 24px of the 32px increase comes out of the preview, taking
 * the resting (sidebar-collapsed, panel-open) render from 85% to 81%. That trade is
 * deliberate and is recorded in the geometry table above — a slightly smaller email with
 * room around it reads better than a slightly larger one wedged between two rails, and
 * "Hide panel" still reaches a true 100%.
 */
const COL_GAP = 8
/** `COL_GAP` in pixels, for the arithmetic below — the `@repo/ui` base is 4px (§29). */
const COL_GAP_PX = COL_GAP * 4

/**
 * Exactly how much width the preview column gains when the panel collapses, and loses when
 * it opens: the rail plus the gap that stops existing with it.
 *
 * MEASURED rather than trusted — at 1280x800 with the sidebar collapsed the preview frame's
 * `clientWidth` is **485 with the panel open and 829 with it hidden**, and 829 - 485 = 344 =
 * 312 + 32. That is not a coincidence to be re-derived: the preview column is `flex: 1` in
 * this row, so it absorbs precisely what the panel gives up, whatever the window and sidebar
 * are doing either side of it.
 *
 * It is used for ONE thing — see `togglePanel`. Everywhere else the frame's width is still
 * measured, never derived, for the reasons the measurement effect's docblock gives.
 */
const PANEL_COLLAPSE_DELTA = PROPS_W + COL_GAP_PX

/**
 * ── The panel's collapse, and why the email has to move on the same clock ──────
 *
 * "Hide panel" used to be `{panelOpen && <rail/>}` — the rail was unmounted, the row
 * reflowed in one frame, and the email jumped from 81% to 100% with it. The user's report
 * was "panel hide should be smooth collapse or close - not flash".
 *
 * The timing is the app's existing right-docked-panel slide, imported rather than re-picked.
 * `AskPanel` is the same gesture (a rail on the right leaving and returning) and its store
 * already records why the constants live in one place: *"Living here rather than being
 * declared twice means the two can never drift apart."* A third declaration of 240ms here
 * would be the drift that sentence is about.
 *
 * ⚠️ **Two things animate, and if they do not share a clock the flash simply moves.** The
 * email is `transform: scale()`d to fit its column (see `previewScale`), so collapsing the
 * rail takes the scale 81% -> 100%. Animate the rail's width alone and the email still snaps,
 * now in the MIDDLE of a smooth layout change, which reads worse than the original jump.
 * `AskPanel`'s own docblock records the identical finding from the other direction — the
 * panel slid while `main` sat still and then snapped, and *"sequential motion reads as a
 * flash however smooth each half is"*.
 *
 * So the rail's `width`/`margin-left` and the email's `transform`/box both carry this exact
 * duration and curve, started in the same frame by the same React commit. They are
 * synchronised BY CONSTRUCTION rather than by a measurement loop chasing the layout — which
 * also matters because a measurement loop is not available here (see `togglePanel`).
 *
 * ## Measured, not asserted (2026-09-05, 1280x800, sidebar collapsed)
 *
 * One toggle creates **7 transitions in a single commit** — rail `width`/`margin-left`/
 * `opacity`/`visibility`, the scale box's `width`/`height`, and the iframe's `transform` —
 * every one of them 240ms on `cubic-bezier(0.32, 0.72, 0, 1)`. Seeking them together
 * (`getAnimations()` + `currentTime`) gives the rail collapsing 312 -> 0 while the email
 * climbs 81% -> 100%, monotonically, no step:
 *
 * | t (ms) |   0 |    40 |    80 |   120 |   160 |   200 |  240 |
 * |--------|-----|-------|-------|-------|-------|-------|------|
 * | rail   | 312 | 147.4 |  38.3 |  14.1 |   4.7 |   1.0 |    0 |
 * | frame  | 485 |   666 |   787 |   813 |   824 |   828 |  829 |
 * | email  | 485 | 545.7 | 585.9 | 594.8 | 598.3 | 599.6 |  600 |
 *
 * The painted email never exceeds the frame, so no scrollbar flashes mid-move. That is
 * structural rather than lucky: both sides ease on one curve, so at fraction `f` the frame
 * is `485 + 344f` and the box `485 + 115f`, and `115f <= 344f` for all `f`. Reopening is the
 * same identity reversed, equal only at `f = 1` (485/485 — an exact fit).
 *
 * ⚠️ **The CCD preview pane cannot show you any of this, and fails in a way that looks like
 * a bug.** Its `document.timeline` advances in ~1000ms QUANTA — four times this duration — so
 * by the next observation every transition has already completed and been dropped from
 * `getAnimations()`. Read it a beat later and you see zero animations and a finished layout,
 * i.e. exactly the instant snap this constant exists to remove. Sampling on a timer or
 * `requestAnimationFrame` cannot fix that (rAF never fires here either — see `togglePanel`).
 * Catch the transitions in the commit's own microtask (a `MutationObserver` on the scale box),
 * `pause()` them, and seek. Do not "re-verify" this by clicking and looking.
 */
const PANEL_SLIDE = `${ASK_PANEL_SLIDE_DURATION_MS}ms ${ASK_PANEL_SLIDE_EASE}`

/**
 * Minimum height for the SMS body region.
 *
 * Sized to the loading skeleton it replaces, so the bubble, the skeleton and the "No SMS
 * equivalent" empty state all occupy the same floor and swapping between them cannot
 * collapse the card. See the SMS card for the rest of the anti-flash treatment.
 */
const SMS_BODY_MIN_H = 72

const isPlainObject = (v: unknown): v is Record<string, any> =>
  v != null && typeof v === 'object' && !Array.isArray(v)

/**
 * Flatten a props object into leaf entries keyed by dot-path. Recurses plain
 * objects (e.g. `scanDetails.tagId`); arrays + primitives are leaves. Lets nested
 * props (like `scanDetails: { tagId, timestamp, event }`) render as individual
 * editable fields instead of a single `[object Object]` input.
 */
const flattenProps = (obj: Record<string, any>, prefix = ''): { path: string; value: any }[] =>
  Object.entries(obj).flatMap(([k, v]) => {
    const path = prefix ? `${prefix}.${k}` : k
    return isPlainObject(v) ? flattenProps(v, path) : [{ path, value: v }]
  })

/** Immutably set a leaf by dot-path, preserving the rest of the structure. */
const setByPath = (obj: Record<string, any>, path: string, value: any): Record<string, any> => {
  const [head, ...rest] = path.split('.')
  if (!rest.length) return { ...obj, [head]: value }
  return { ...obj, [head]: setByPath(isPlainObject(obj[head]) ? obj[head] : {}, rest.join('.'), value) }
}

/**
 * ── Filling a template's product props from a REAL product (VRG-560) ─────────
 *
 * Which prop paths a product fills is READ OFF THE TEMPLATE, never assumed. The
 * registry carries product data in two shapes and the split is not a mistake to
 * be normalised — it mirrors the two payload shapes the real senders build:
 *
 *   nested   `product.title` / `product.sku` / `product.imgLink`     (~19 templates)
 *   flat     `productTitle`  / `productSku`  / `productImgUrl`       (~2 templates)
 *
 * So this maps a FLATTENED prop path to the product field it wants, and returns
 * `undefined` for anything that is not a product path. A template with no product
 * paths gets no picker at all rather than a control that writes nowhere — which is
 * the failure mode a hardcoded list of paths would have produced the first time a
 * template used a third spelling.
 *
 * `name` is accepted alongside `title` because `EmailProductCard` reads either.
 */
type ProductPropField = 'title' | 'sku' | 'image'

const productFieldForPath = (path: string): ProductPropField | undefined => {
  const segment = path.split('.').pop() ?? ''
  // `product.title` -> `title`; `productTitle` -> `Title`. Anything else is not a
  // product path — `customer.title` must not match, hence the anchored prefix test
  // rather than a substring search.
  const key = /^product\./i.test(path)
    ? segment
    : /^product[A-Z]/.test(segment)
      ? segment.slice('product'.length)
      : null
  if (key === null) return undefined

  switch (key.toLowerCase()) {
    case 'title':
    case 'name':
      return 'title'
    case 'sku':
      return 'sku'
    case 'imglink':
    case 'imgurl':
    case 'image':
    case 'imageurl':
      return 'image'
    default:
      return undefined
  }
}

/** The product shape the picker hands back — `ProductPreview`'s own fields. */
interface SampleProduct {
  id: string
  title?: string | null
  sku?: string | null
  images?: string[] | null
}

const productValue = (product: SampleProduct, field: ProductPropField): string => {
  switch (field) {
    case 'title':
      return product.title ?? ''
    case 'sku':
      return product.sku ?? ''
    case 'image':
      return product.images?.[0] ?? ''
  }
}

/**
 * Labs → Notifications: a browse + test surface for every notification — every
 * React-Email template, plus its SMS equivalent where one exists (VRG-512).
 * Shows the selected template's server-rendered email preview, editable props, a
 * real send-test (dev → Mailpit), and the composed SMS body below it (or "No SMS
 * equivalent" when the template isn't in SMS_TEMPLATE_BODY_MAP). Renamed from
 * "Emails" the same day SMS was added: "Emails" undersold what's here once a
 * second channel showed up.
 *
 * ## Layout — three columns, and why it stopped being a chip row (VRG-560)
 *
 * The template index used to be a HORIZONTAL, category-grouped chip row inside the
 * sticky header, with a sliding pill under the selected chip. At 47 templates that
 * had stopped being navigation: at 1280 a category label rendered as "SIG…", and the
 * only way to learn what existed was to drag a strip sideways. It is now a VERTICAL
 * left column on the shared `SideNav` rail — the same construction the docs
 * table-of-contents uses (`DocSectionNav`), for the same reason: a long GROUPED list
 * where the question is "what is in here" rather than "which of these three".
 *
 * The search field moved to the HEAD of that column, where it filters the list it
 * sits above. The sliding pill is gone with the row that carried it; the rail marks
 * the selected row itself, so nothing needed to replace it.
 *
 * ⚠️ This index stays a column of THIS section, and is deliberately not folded into the
 * Labs section nav. Those are different levels: the section nav is NAVIGATION between
 * Labs surfaces, this is CONTENT SELECTION within one of them. Merging them would also
 * put ~61 rows (47 templates under ~14 category headings) in a list with 8, so either the
 * section rows scroll out of reach or one column carries two competing scroll regions.
 * The width the section needed came from `LabsPage` dropping its nav COLUMN for a tab
 * row, which costs vertical space nobody was using.
 *
 * ## The right panel, and its order (VRG-560)
 *
 * One collapsible 312px rail holding, top to bottom: **Company**, the composed SMS body,
 * then Props. Company is first because it scopes the two cards under it.
 *
 * ### What the second pass removed, and why it was worse than incomplete
 *
 * The rail used to open with a free-text **Company name** field and a **White label**
 * switch, sitting inside the SMS card. Measured: they fed exactly one request
 * (`/…/sms-preview`) and nothing else, and this docblock previously said so in as many
 * words — *"They do NOT affect the email."*
 *
 * That is a control pair that lets you SIMULATE a brand state, on a surface whose entire
 * job is to show you a real one. Two consequences, both live:
 *
 *  - the SMS body composed a sender prefix from a name nobody had and a flag nothing set,
 *    so it previewed a text message no brand would ever send;
 *  - the EMAIL half had no company at all, so `/preview` passed no `brandTokens` and a
 *    brand logo could never appear here — while `providerSend` puts one on every real
 *    send with a resolvable company. The preview was structurally unable to show the one
 *    thing someone checking a white-label email opens it to check.
 *
 * Both are now READ from a picked company: the name from `companies`, the white-label
 * flag from `certificate_trigger_settings.hide_powered_by`, the marks + Studio theme from
 * the same mapping the real send uses, and the two link hosts from the same resolvers the
 * send and scan paths call. See `BrandContextCard` and
 * `GET /internal/labs/companies/:id/context`.
 *
 * No company picked is a supported state, not a blocked one: the lab opens that way and
 * renders unbranded, which is exactly what a template with no resolvable company produces
 * on a real send.
 *
 * SMS moved OUT of the preview column (it was below the email, where a tall template put
 * it below the fold) and Props moved out of the same place before it. The preview column
 * is now only the email and the controls that act on the email, which is what makes the
 * 600px card the widest thing that column has to hold.
 */
export const NotificationsLab = () => {
  /**
   * This section publishes NOTHING for the layout to pin against any more (VRG-560).
   *
   * It used to measure its own sticky band and publish `--labs-band-h`, so the TOC column
   * could pin below the page header PLUS the band. Deleting the band deleted that second
   * term: the column pins at `LABS_CONTENT_TOP`, which is the page header's height alone,
   * measured and published once by `LabsPage`. See `LABS_CONTENT_TOP` for why one offset
   * beats a sum of two, and `LabsSideColumn` for where the band's contents went.
   */
  const { open } = useSnackbar()
  const { data: profile } = useUserProfileQuery()
  const { resolvedMode } = useColorMode()
  const [searchParams, setSearchParams] = useSearchParams()
  const [templates, setTemplates] = useState<EmailTemplate[]>([])
  const [activeGroup, setActiveGroup] = useState<'app' | 'platform'>(
    searchParams.get('group') === 'platform' ? 'platform' : 'app',
  )
  const [search, setSearch] = useState('')
  const [selectedId, setSelectedId] = useState('')
  const [props, setProps] = useState<Record<string, any>>({})
  const [to, setTo] = useState('')
  const [dark, setDark] = useState(resolvedMode === 'dark')
  const [previewHtml, setPreviewHtml] = useState('')
  const [loadingList, setLoadingList] = useState(true)
  const [loadingPreview, setLoadingPreview] = useState(false)
  // The iframe sizes itself to its actual content rather than a fixed box.
  // A fixed height (previously 600) left a gap below any email shorter than
  // that — e.g. "Scan by anonymous" — and the raw HTML has no background on
  // <html>/<body> by design (base-email.tsx: real email clients strip <body>
  // and lose anything styled there, so the visible background is painted on
  // an inner Section instead). With no author background AND
  // `color-scheme: light dark` in the email's own <meta> (also deliberate —
  // it stops email clients from auto-inverting content we've already themed
  // both ways ourselves), a browser rendering that gap defaults to ITS dark
  // scheme's canvas fill whenever the OS/browser prefers dark — a solid black
  // bar, regardless of this Lab's own light/dark toggle. Real email clients
  // never hit this: their reading pane sizes to content, so there's no gap to
  // paint. Fixing it here (not in base-email.tsx) is deliberate — the
  // template's background choices are correct for real clients; the bug is
  // this preview's fixed-height box, so that's what changes.
  const [previewHeight, setPreviewHeight] = useState(400)
  /**
   * ── The frame's fill IS the email's own page background ──────────────────────
   *
   * The email paints TWO surfaces, not one: an outer `.v-bg` Section carrying the page
   * tone with `padding: 24px 0`, and the `.v-card` container inside it. So the top and
   * bottom 24px of every rendered email are page-background, and at 81% scale that is a
   * ~19px horizontal band across the full width of the iframe.
   *
   * The frame used to fill itself with `#ffffff` / `#0F0F11`, neither of which is that
   * tone — so the band was bounded by a colour discontinuity on BOTH edges and read as a
   * stripe. Measured at 1280x800, light mode, down the frame's centre line:
   *
   *   y=419  rgb(255,255,255)  the frame
   *   y=421  rgb(244,244,245)  .v-bg      <- band starts, step of 11
   *   y=439  rgb(255,255,255)  .v-card    <- band ends, step of 11
   *
   * Dark was the same defect with a smaller step: frame `#0F0F11` against a `#09090B`
   * band. Subtle enough to survive one review, still wrong.
   *
   * ⚠️ A PREVIOUS PASS MEASURED THIS AND CONCLUDED IT WAS CORRECT — "the email's own
   * 24px outer padding, what a real mail client shows" — and left it. The padding half
   * of that is true and is NOT what changed here: the band still exists and still
   * belongs to the email. What was wrong is the SURROUND. A mail client's reading pane
   * does not paint a third colour either side of the message's own page background,
   * which is exactly what made this read as a seam rather than as margin. Do not
   * re-derive the earlier conclusion from the padding alone; the padding was never the
   * defect.
   *
   * **Read from the rendered email, never retyped.** This holds the value read off the
   * `.v-bg` element itself in `handlePreviewLoad`. The tokens live in `@repo/emails`
   * (`defaultEmailTokens` / `darkEmailTokens` `.backgroundColor`) and the platform
   * CANNOT import them: that package's only export is a barrel over the whole react-email
   * template corpus, so reaching two constants would pull the entire tree into this
   * bundle. Copying the hexes here is the drift this epic has spent the day removing.
   *
   * Reading the computed style is strictly stronger than importing the token anyway: it
   * is the value the band ACTUALLY paints, so it follows the email through anything that
   * changes it after render — a brand token, or the `prefers-color-scheme` block in
   * `base-email.tsx` that rewrites `.v-bg` with `!important`. An imported constant states
   * intent; this states outcome, and a seam is an outcome.
   *
   * `undefined` until the first email loads — the frame is transparent for that one
   * frame, under the loading spinner. Deliberately NOT cleared when `dark` flips: the
   * iframe keeps showing the OLD email until the new `srcDoc` finishes loading, so
   * holding the old colour keeps frame and email in agreement the whole way across, and
   * `handlePreviewLoad` swaps both in the same commit. Clearing it would put a
   * transparent gap under an email that is still on screen.
   */
  const [previewPageBg, setPreviewPageBg] = useState<string>()
  const previewIframeRef = useRef<HTMLIFrameElement>(null)
  const [sending, setSending] = useState(false)
  // Side panel open/closed. Open by default — a panel nobody can see is the defect
  // VRG-560 set out to fix — and it now fits alongside a full 600px email at 1280 with the
  // platform sidebar collapsed. It stays collapsible because that is not true with the
  // sidebar expanded, and because a reader comparing two templates' pixels wants the whole
  // window (see the column-geometry note above).
  const [panelOpen, setPanelOpen] = useState(true)

  /**
   * ── Preview zoom (VRG-560) ──────────────────────────────────────────────
   *
   * The email card is a FIXED 600px and the preview column is not. Before this, a column
   * narrower than 600 meant a horizontally-scrolled email — which happened on every
   * arrangement except "sidebar collapsed AND panel open" (measured 383px with the
   * sidebar expanded, i.e. under two thirds of the card). Reviewing 47 templates through
   * a 383px window onto a 600px artifact is not reviewing them.
   *
   * So the preview ALWAYS scales to fit its column. There is no toggle.
   *
   * ## The "Fit width" switch is gone, and its 1:1 job moved to "Hide panel"
   *
   * It shipped alongside `Dark` as the way back to true pixels, and was removed the same
   * day on the user's "what is the fit width toggle? - remove it." The behaviour it named
   * is the one people want ~always, so it was a switch whose ON position was correct for
   * every case anyone had — and whose OFF position produced the horizontally-scrolled
   * email this whole treatment exists to prevent. A control with one useful position is a
   * control that only costs a reader the moment spent deciding it does not concern them.
   *
   * True 1:1 is not lost: **"Hide panel" reaches a clamped 100% in both sidebar states**
   * (843px collapsed, 635px expanded, each ≥ `EMAIL_W`), so the escape hatch is a control
   * that already existed and already earned its place. See the column-geometry note.
   *
   * The live percentage next to the EMAIL caption is what keeps this honest — a scaled
   * preview must never pass for a true-size one, and with the toggle gone that readout is
   * the ONLY thing on screen saying so.
   *
   * `frameW` is the preview frame's own content width, measured rather than derived from
   * the column arithmetic — the arithmetic has four inputs this component does not own
   * (sidebar state, window width, the Layout gutter, the panel toggle) and would go stale
   * the same way the Labs header's hardcoded 64 did.
   */
  const previewFrameRef = useRef<HTMLDivElement>(null)
  const [frameW, setFrameW] = useState(0)

  const [smsPreview, setSmsPreview] = useState<{ hasSms: boolean; body: string | null } | null>(null)
  const [loadingSms, setLoadingSms] = useState(false)

  /**
   * ── The company this lab previews AGAINST (VRG-560, second pass) ──────────────
   *
   * This state replaces two controls that were removed together: a free-text
   * `companyName` and a `whiteLabel` switch. Measured before removing them, they
   * fed exactly ONE request (`/sms-preview`) and nothing else — this file's own
   * docblock said so — so they let a reader SIMULATE a brand rather than see one,
   * and the email half of the surface had no company at all. That is why a brand
   * logo could never appear in this preview while appearing on every real send.
   *
   * Everything the pair used to fake is now resolved server-side from the picked
   * company: its name, its `hide_powered_by` flag, and the hosts its links point
   * at. See `GET /internal/labs/companies/:id/context`.
   *
   * `null` is a real, supported state — the lab opens with no company and shows an
   * unbranded render, which is exactly what a template with no resolvable company
   * produces on a real send. Nothing here is gated on picking one.
   */
  const [company, setCompany] = useState<BrandContextCompany | null>(null)
  const [brandContext, setBrandContext] = useState<BrandContext | null>(null)
  const [loadingContext, setLoadingContext] = useState(false)

  /**
   * The internal company list, from the SAME hook the internal Companies page uses
   * rather than a Labs-only endpoint (§28). `limit: 200` because this is a picker,
   * not a paged table: its own type-to-filter search is the navigation, and a
   * second page nobody can reach would silently hide companies from it.
   */
  const { data: companiesPage } = useCompaniesQuery({ limit: 200 })
  /**
   * Each row is `{ company, plan, counts }` — the company is NESTED. `useCompaniesQuery`
   * used to type the rows as `Company[]`, so building the picker on the row compiled and
   * rendered nameless rows (this did, on first run). VRG-706 typed the rows as
   * `CompanyListRow` and moved every picker onto `listedCompanies`.
   */
  const companies = useMemo<BrandContextCompany[]>(() => listedCompanies(companiesPage), [companiesPage])

  useEffect(() => {
    if (profile?.email) setTo(prev => prev || profile.email)
  }, [profile?.email])

  /**
   * True for the length of the panel's collapse/expand. While it is set, every measurement
   * of the preview frame is REFUSED — see the effect and `togglePanel` below for why a
   * measurement taken mid-transition is worse than no measurement at all.
   */
  const panelAnimatingRef = useRef(false)
  const panelAnimationTimerRef = useRef<number>()
  /**
   * The same fact as the ref, as state, because it is needed in two places that cannot share
   * one carrier: `measure` reads it SYNCHRONOUSLY inside a `ResizeObserver` callback (a ref,
   * since a state read there would be a stale closure), and the email's `sx` needs it to
   * RE-RENDER (a ref change paints nothing).
   *
   * The email's transition is scoped to this flag rather than left always-on, and that is
   * deliberate. The scale box's `height` tracks `previewHeight`, which also changes every time
   * a different template loads — walking Prev/Next through 47 of them. An always-on transition
   * would ease the frame's height on every one of those, which is a slower-feeling change to
   * the surface's most frequent interaction, asked for by nobody.
   */
  const [panelAnimating, setPanelAnimating] = useState(false)

  /**
   * Measure the preview frame's content width, which is what the email is scaled against.
   *
   * Two triggers, because neither covers every way this width moves:
   *  - `ResizeObserver`, for the changes React never sees: the platform sidebar
   *    collapsing 288 -> 80, which is app-level state this component does not re-render on;
   *  - `window.resize`, as the belt to the observer's braces.
   *
   * `clientWidth` rather than `getBoundingClientRect().width` deliberately — it excludes
   * a vertical scrollbar's gutter, which is the width actually available to paint into.
   *
   * ## `panelOpen` is deliberately NOT a trigger any more, and the guard is not belt-and-braces
   *
   * It used to be in this effect's deps, on the reasoning that the panel appearing or leaving
   * is the largest single change to this width and is React-driven, so measuring here caught
   * it synchronously before paint. That was correct while the panel was unmounted in one
   * frame. It is now actively wrong, in BOTH mechanisms:
   *
   *  - Re-running here on `panelOpen` measures at the instant the class flips, when the CSS
   *    transition has not moved anything yet — i.e. it reads the width the frame is LEAVING,
   *    and then nothing ever re-measures it. The email would settle at the previous scale and
   *    stay there.
   *  - `ResizeObserver` DOES fire per frame through a transition, and letting it through would
   *    be worse still: the scale would chase the layout one frame behind while its own
   *    transition aimed at a target that kept moving. That is a rubber-band, not a collapse.
   *
   * So the toggle publishes the destination itself (`togglePanel`) and this effect stands down
   * until the transition is over. `selectedId` and `loadingList` stay in the deps: they change
   * WHICH element `previewFrameRef` points at (skeleton frame vs real frame), which is a
   * different question from what that element currently measures.
   */
  useLayoutEffect(() => {
    const el = previewFrameRef.current
    if (!el) return
    const measure = () => {
      if (panelAnimatingRef.current) return
      setFrameW(el.clientWidth)
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    window.addEventListener('resize', measure)
    return () => {
      ro.disconnect()
      window.removeEventListener('resize', measure)
    }
  }, [loadingList, selectedId])

  /**
   * Open or collapse the panel, and move the email's scale onto the same clock.
   *
   * ## The scale is PREDICTED, not measured, and only here
   *
   * `previewScale` is `frameW / 600`, and `frameW` is measured. A transition means the frame's
   * real width is only correct at the END, so a measured scale can only ever snap late. The
   * destination, though, is known exactly the moment the click happens: the preview column is
   * `flex: 1`, so it gains precisely the rail plus its gap (`PANEL_COLLAPSE_DELTA`, measured —
   * see that constant). Publishing it here means React commits the FINAL scale in the same
   * frame the rail starts moving, and the email's own `transform` transition carries it there
   * over the same duration and curve. Nothing chases anything.
   *
   * This is the one place the width is derived rather than measured, and it is bounded: a
   * delta, applied to a measured base, reconciled against a real measurement the moment the
   * transition ends. The general objection to deriving this width — that the arithmetic has
   * four inputs this component does not own — does not apply to a delta, because three of
   * those four inputs (sidebar, window, page gutter) are unchanged across the toggle.
   *
   * ## Why not just measure every frame instead
   *
   * A `requestAnimationFrame` loop sampling `clientWidth` would couple the two by measurement
   * rather than by construction, and it was rejected on two counts. It is a frame behind by
   * definition (it can only report what the layout already did). And it does not run at all in
   * the Claude preview pane, where `requestAnimationFrame` fires ZERO times and
   * `document.timeline.currentTime` stays at 0 — verified here, 0 callbacks in 7.8s. A
   * scale driven by that loop would never leave its starting value in that environment: not a
   * degraded animation, a permanently wrong scale. Prediction is correct with no frames at all.
   *
   * ## The release timer does NOT measure, and that is the point
   *
   * `transitionend` reconciles (below, on the rail). It cannot be the only release, because
   * under `prefers-reduced-motion` there is no transition and therefore no event — the guard
   * would latch and every later resize would stop updating the scale. So a timer releases it.
   * The timer only clears the flag; it never re-measures. In the preview pane above, layout is
   * frozen until something forces a frame, so a measurement on a timer would read the
   * pre-transition width and overwrite the correct prediction with a stale one. Releasing
   * without measuring leaves the prediction standing and lets the next genuine resize correct
   * it, which is right in every combination of (motion, no motion) x (frames, no frames).
   */
  const togglePanel = () => {
    const next = !panelOpen
    setFrameW(w => (w > 0 ? w + (next ? -PANEL_COLLAPSE_DELTA : PANEL_COLLAPSE_DELTA) : w))
    panelAnimatingRef.current = true
    setPanelAnimating(true)
    window.clearTimeout(panelAnimationTimerRef.current)
    panelAnimationTimerRef.current = window.setTimeout(() => {
      panelAnimatingRef.current = false
      setPanelAnimating(false)
    }, ASK_PANEL_SLIDE_DURATION_MS + 120)
    setPanelOpen(next)
  }

  useEffect(() => () => window.clearTimeout(panelAnimationTimerRef.current), [])

  // Load the registry once; honor the URL's ?group=/?template= for deep-links,
  // else default to the first App template.
  useEffect(() => {
    let active = true
    api
      .get('/internal/labs/emails')
      .then(({ data }) => {
        if (!active) return
        const list: EmailTemplate[] = data?.templates ?? []
        setTemplates(list)
        const urlGroup =
          searchParams.get('group') === 'platform' ? 'platform' : searchParams.get('group') === 'app' ? 'app' : null
        const urlTemplate = searchParams.get('template')
        const fromUrl = urlTemplate ? list.find(t => t.id === urlTemplate) : undefined
        const grp: 'app' | 'platform' = urlGroup ?? (fromUrl?.group === 'platform' ? 'platform' : 'app')
        const first = fromUrl ?? list.find(t => inTab(t, grp)) ?? list[0]
        if (first) {
          setActiveGroup(grp)
          selectInternal(first)
        }
      })
      .catch(() => active && setTemplates([]))
      .finally(() => active && setLoadingList(false))
    return () => {
      active = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Reflect the active group + selected template in the URL query (deep-linkable,
  // replace so it doesn't spam history).
  const updateParams = (patch: { group?: string; template?: string }) => {
    setSearchParams(
      prev => {
        const p = new URLSearchParams(prev)
        if (patch.group) p.set('group', patch.group)
        if (patch.template) p.set('template', patch.template)
        return p
      },
      { replace: true },
    )
  }

  const query = search.trim().toLowerCase()

  // Visible set: search spans all groups; otherwise the active App/Platform tab.
  const visible = useMemo(
    () =>
      query
        ? templates.filter(t => t.name.toLowerCase().includes(query) || t.category.toLowerCase().includes(query))
        : templates.filter(t => inTab(t, activeGroup)),
    [templates, query, activeGroup],
  )

  // Group by category (alpha), templates alpha within.
  const grouped = useMemo(() => {
    const byCat = new Map<string, EmailTemplate[]>()
    for (const t of visible) {
      if (!byCat.has(t.category)) byCat.set(t.category, [])
      byCat.get(t.category)!.push(t)
    }
    return Array.from(byCat.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([category, items]) => ({ category, items: items.sort((a, b) => a.name.localeCompare(b.name)) }))
  }, [visible])

  const selected = templates.find(t => t.id === selectedId)

  /**
   * The visible index flattened into one ordered walk, for Prev/Next (VRG-560).
   *
   * Derived from `grouped`, NOT from `templates`, so the walk follows exactly what the TOC
   * shows: the same category order, the same alphabetical order within a category, and the
   * same set under an active search. Reviewing 47 templates one at a time is the surface's
   * main use, and returning to the column to click the next row every time is the friction
   * this removes — but only if "next" means the row visibly below, which is why it reads
   * the rendered structure rather than re-sorting the registry.
   */
  const ordered = useMemo(() => grouped.flatMap(g => g.items), [grouped])
  // -1 when the selection sits outside the current filter — reachable by searching after
  // selecting. Both controls disable and say so rather than silently jumping the user to
  // an unrelated template.
  const orderIndex = ordered.findIndex(t => t.id === selectedId)
  const prevTemplate = orderIndex > 0 ? ordered[orderIndex - 1] : undefined
  const nextTemplate = orderIndex >= 0 && orderIndex < ordered.length - 1 ? ordered[orderIndex + 1] : undefined

  function selectInternal(t: EmailTemplate) {
    setSelectedId(t.id)
    setProps({ ...(t.sampleProps ?? {}) })
    updateParams({ template: t.id })
  }

  const selectTemplate = (id: string) => {
    const t = templates.find(x => x.id === id)
    if (t) selectInternal(t)
  }

  const selectGroup = (g: string) => {
    setSearch('')
    setActiveGroup(g as 'app' | 'platform')
    updateParams({ group: g })
    const first = templates.filter(t => inTab(t, g as 'app' | 'platform')).sort((a, b) => a.name.localeCompare(b.name))[0]
    if (first) selectInternal(first)
  }

  const propsKey = JSON.stringify(props)

  /**
   * ── The sample-product picker's three derived values (VRG-560) ────────────────
   *
   * Declared HERE, above the loading early-return, because they are hooks and a hook
   * below a conditional return would change order between renders.
   */
  /** Which of THIS template's prop paths a product can fill. Empty ⇒ no picker. */
  const productPropPaths = useMemo(
    () =>
      flattenProps(props)
        .map(({ path }) => ({ path, field: productFieldForPath(path) }))
        .filter((entry): entry is { path: string; field: ProductPropField } => !!entry.field),
    [propsKey], // eslint-disable-line react-hooks/exhaustive-deps
  )
  const sampleProducts = useMemo<SampleProduct[]>(
    () => brandContext?.products ?? [],
    [brandContext],
  )
  const [sampleProduct, setSampleProduct] = useState<SampleProduct | null>(null)

  /**
   * Write every product path this template has, in ONE state update.
   *
   * Sequential `setProp` calls would each read a stale `props` from the closure and
   * the last one would win, so a three-field template would end up with one field
   * filled — the bug this fold exists to avoid rather than to fix later.
   *
   * Clearing the pick deliberately leaves the props as they are. The alternative is
   * restoring the registry's sample values, which would silently discard anything
   * the reader typed by hand into the same fields; "clear the selection" is a
   * statement about the picker, not an instruction to revert the form.
   */
  const applySampleProduct = (picked: SampleProduct | null) => {
    setSampleProduct(picked)
    if (!picked) return
    setProps(previous =>
      productPropPaths.reduce(
        (accumulated, { path, field }) => setByPath(accumulated, path, productValue(picked, field)),
        previous,
      ),
    )
  }

  /**
   * A pick belongs to the company it came from. Left standing across a company
   * change, the field would show one brand's product while the panel above it named
   * another — and the props it wrote would already be in the email.
   */
  useEffect(() => {
    setSampleProduct(null)
  }, [company?.id])

  /**
   * Resolve the picked company's brand + host context.
   *
   * Deliberately NOT folded into the preview request. The preview re-fires on every
   * keystroke in the Props card (`propsKey`) and on every template step through
   * Prev/Next; the company context changes only when the COMPANY changes, and
   * re-resolving four lookups per keystroke to redraw the same two hosts would be
   * work nobody asked for. Separate effects, separate cadences.
   *
   * Fails to `null` rather than surfacing an error: the card's own loading branch
   * already renders a skeleton for "not here yet", and a company whose context
   * cannot be read is not a reason to break the email preview beside it.
   */
  useEffect(() => {
    if (!company?.id) {
      setBrandContext(null)
      setLoadingContext(false)
      return
    }
    let active = true
    setLoadingContext(true)
    api
      .get(`/internal/labs/companies/${company.id}/context`)
      .then(({ data }) => active && setBrandContext(data))
      .catch(() => active && setBrandContext(null))
      .finally(() => active && setLoadingContext(false))
    return () => {
      active = false
    }
  }, [company?.id])

  // Server-render the preview when template / props / mode / company change.
  useEffect(() => {
    if (!selectedId) return
    let active = true
    setLoadingPreview(true)
    api
      .get(`/internal/labs/emails/${selectedId}/preview`, {
        responseType: 'text',
        // `companyId` is what brands the render — the server maps that company's
        // Studio theme + marks onto the email's tokens, exactly as `providerSend`
        // does. Omitted when no company is picked, which renders unbranded.
        params: { props: propsKey, mode: dark ? 'dark' : 'light', companyId: company?.id },
      })
      .then(({ data }) => active && setPreviewHtml(typeof data === 'string' ? data : ''))
      .catch(
        () =>
          active &&
          setPreviewHtml('<p style="padding:24px;font-family:sans-serif;color:#b00">Preview failed to render.</p>'),
      )
      .finally(() => active && setLoadingPreview(false))
    return () => {
      active = false
    }
  }, [selectedId, propsKey, dark, company?.id])

  // VRG-512 — server-compose the SMS equivalent alongside the email preview.
  // Skips the round trip entirely when the list already says this template
  // has no SMS mapping (`hasSms`), rather than firing a request that would
  // just come back `{hasSms: false, body: null}`.
  useEffect(() => {
    if (!selectedId) {
      setSmsPreview(null)
      return
    }
    const template = templates.find(t => t.id === selectedId)
    if (!template?.hasSms) {
      setSmsPreview({ hasSms: false, body: null })
      /**
       * Clearing the flag on the short-circuit path matters, and it is not defensive
       * bookkeeping — it fixes a state the panel could get permanently stuck in.
       *
       * The in-flight branch clears `loadingSms` in a `.finally` guarded on `active`, so a
       * request whose effect has already been torn down deliberately does NOT clear it —
       * correct, because the SUCCEEDING effect owns the flag from then on. But this branch
       * returns without ever running that `.finally`, so it inherited a `true` and never
       * put it down: the card rendered its loading skeleton forever instead of "No SMS
       * equivalent".
       *
       * Reachable whenever a template WITH an SMS body is left before its request settles
       * and one WITHOUT is selected next. Pre-existing (VRG-512), and previously rare
       * because switching meant a deliberate trip to the index; the Prev/Next walk added
       * in VRG-560 makes rapid switching the surface's main interaction, so it started
       * happening on the second click. Observed live before this line: the SMS card held a
       * 72px skeleton indefinitely while the network tab showed every request had returned
       * 200.
       */
      setLoadingSms(false)
      return
    }
    let active = true
    setLoadingSms(true)
    api
      .get(`/internal/labs/emails/${selectedId}/sms-preview`, {
        // VRG-560 — one id instead of a name and a switch. The server resolves the
        // sender prefix (`companies.name` + `hide_powered_by`) and the link host
        // from it, so the composed body is the one this brand would actually send
        // rather than one assembled from typed-in values.
        params: { props: propsKey, companyId: company?.id },
      })
      .then(({ data }) => active && setSmsPreview(data))
      .catch(() => active && setSmsPreview({ hasSms: true, body: null }))
      .finally(() => active && setLoadingSms(false))
    return () => {
      active = false
    }
  }, [selectedId, propsKey, company?.id, templates])

  const setProp = (path: string, value: string) => setProps(p => setByPath(p, path, value))

  // Measures the ACTUAL rendered email each time a new one loads, rather than
  // trusting a guessed constant — see the `previewHeight` state comment for
  // why a fixed box is what caused the black-bar bug in the first place.
  // Clamped: a floor so the box doesn't collapse to near-nothing while a very
  // short template's content is still settling, a ceiling (with `overflowY:
  // auto` on the container) so a malformed or unusually tall template can't
  // blow the panel out — normal templates land well under either bound.
  const handlePreviewLoad = () => {
    // Re-measure the frame here as well as in the layout effect. Belt to the
    // ResizeObserver's braces, and the only one of the two that is cheap to reason about:
    // the platform sidebar's collapsed/expanded state (and its hover-`auto` mode) lives in
    // `Sidebar`'s own `useState`, exposed through no context or store, so this component
    // CANNOT re-render on it — the observer is the only thing that sees that 208px swing.
    // Firing here means that even if an observer callback is ever missed, the scale
    // self-corrects on the next template selection, which on a surface built around
    // Prev/Next is the dominant interaction.
    if (previewFrameRef.current) setFrameW(previewFrameRef.current.clientWidth)
    const doc = previewIframeRef.current?.contentDocument
    if (!doc?.body) return
    // The frame adopts the email's page tone so the email's own 24px top/bottom band has
    // nothing to be a seam against — see `previewPageBg`. `.v-bg` is the element that
    // paints that band, so this is the value itself rather than a second copy of it.
    // Guarded rather than assumed: the request-failure fallback HTML has no `.v-bg`, and
    // overwriting with `undefined` there would flash the frame transparent under an error
    // message. Keeping the previous tone is the quieter wrong answer of the two.
    const bg = doc.querySelector('.v-bg')
    if (bg) setPreviewPageBg(doc.defaultView?.getComputedStyle(bg).backgroundColor || undefined)
    // `documentElement.scrollHeight` (i.e. <html>) is NOT safe here — the root
    // element's clientHeight always fills the iframe's CURRENT viewport, so
    // its scrollHeight floors at whatever height was already applied and
    // never reports a SHORTER new document (measured: switching from a 623px
    // email to a 558px one still read back 623). <body> has no such floor.
    setPreviewHeight(Math.min(Math.max(doc.body.scrollHeight, 200), 900))
  }

  const sendTest = async () => {
    if (!selectedId) return
    setSending(true)
    try {
      const { data } = await api.post(`/internal/labs/emails/${selectedId}/test`, {
        to: to.trim() || undefined,
        props,
      })
      open({
        type: 'success',
        title: 'Test email sent',
        render: () => (
          <Typography variant="body2">
            Sent to {data.to}.{' '}
            <Link href={data.mailpitUrl} target="_blank" rel="noreferrer" sx={{ fontWeight: 600 }}>
              Open Mailpit
            </Link>
          </Typography>
        ),
      })
    } catch (e: any) {
      open({ type: 'error', title: 'Send failed', text: e?.response?.data?.message || 'Could not send the test email.' })
    } finally {
      setSending(false)
    }
  }

  /**
   * Fit-to-width scale factor. Never above 1 — a 900px column shows the email at its true
   * 600, centred, rather than blowing a 600px design up to fill space it was not drawn for.
   * That clamp is what makes "Hide panel" a genuine 1:1 escape hatch rather than just a
   * bigger scaled preview (see the zoom note above).
   *
   * `frameW > 0` guards the single render before the layout effect measures; at 0 the
   * scale would be 0 and the email would vanish for a frame.
   *
   * Declared ABOVE the loading early-return on purpose: the skeleton's preview box uses
   * the same factor, so the placeholder reserves the height the scaled email will
   * actually occupy rather than an unscaled 400 (§19 footprint parity).
   */
  const previewScale = frameW > 0 ? Math.min(1, frameW / EMAIL_W) : 1
  const scalePct = Math.round(previewScale * 100)

  if (loadingList) {
    /**
     * Footprint-parity skeleton (§19) — it mirrors the THREE-COLUMN layout, not a list.
     *
     * Rewritten twice now, both times because the LAYOUT moved under it. First when the
     * index became a column (it had been drawing a tab row over eight full-width rows, so
     * it would have loaded one column and jumped to three); and now again with the sticky
     * band deleted (VRG-560) — a skeleton still drawing that band would reserve a 53px
     * strip plus its 20px trailing gap that nothing fills, and every column below would
     * ride 73px UP on load. §19 parity cuts both ways: a region too MANY shifts the layout
     * exactly as a missing one does, which is the trap on a change that removes chrome.
     *
     * Every measurement is the real one — the same `PROPS_W`/`COL_GAP` constants the
     * loaded layout uses, `LabsSideColumnSkeleton` reading the same `LABS_TOC_W` and head
     * spacing as `LabsSideColumn` itself, and the side panel present only when `panelOpen`
     * so a reader who collapsed it does not watch a rail flash in and out on every reload.
     * The panel mirrors BOTH its cards (SMS above Props), because a single block there
     * would grow into two on load.
     */
    return (
      <Box>
        {/* No band, and no `pt` — see the loaded layout's note. The gap above this row is
            the page header block's own `pb`. */}
        <Stack direction="row" spacing={COL_GAP} sx={{ alignItems: 'flex-start' }}>
          {/* Scope track, search field, then three category groups of rail rows — the
              skeleton lives beside the component it stands in for and reads its constants,
              so the two cannot drift into the disagreement that causes a load shift. */}
          <LabsSideColumnSkeleton />

          {/* Preview column: the control row (caption left; Prev/Next, appearance and the
              panel toggle right), the title/subject block beside the send-test pair, then
              the preview box. */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* 48 and `mb: 1.5`, matching the loaded control row's `minHeight` and margin.
                It was 32 while that row held only a caption and Prev/Next; the two controls
                that arrived from the deleted band took it to the 48px toggle track, and a
                skeleton left at 32 would put everything below it 16px high on load. */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ height: 48, mb: 1.5 }}>
              <Skeleton variant="rounded" width={54} height={13} />
              <Stack direction="row" spacing={3} alignItems="center">
                <Stack direction="row" spacing={2} alignItems="center">
                  <Skeleton variant="circular" width={28} height={28} />
                  <Skeleton variant="rounded" width={54} height={14} />
                  <Skeleton variant="circular" width={28} height={28} />
                </Stack>
                {/* The appearance track (48 tall) and the Hide-panel button (40) — the two
                    controls this row gained from the band. */}
                <Skeleton variant="rounded" width={88} height={48} />
                <Skeleton variant="rounded" width={118} height={40} />
              </Stack>
            </Stack>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-end"
              sx={{ mb: 3 }}
            >
              {/* ONE line, not two. The `Title:` line above the subject is deleted
                  (VRG-560), so a two-bar skeleton here would reserve a row that never
                  arrives and drop everything below it ~24px on load. The chip that sat
                  on the deleted row moves onto this one, so it moves here too. */}
              <Stack direction="row" spacing={2} alignItems="center">
                <Skeleton variant="rounded" width={260} height={26} />
                <Skeleton variant="rounded" width={38} height={20} />
              </Stack>
              <Stack direction="row" spacing={3} alignItems="flex-end">
                <Skeleton variant="rounded" width={260} height={49} />
                <Skeleton variant="rounded" width={104} height={49} />
              </Stack>
            </Stack>
            {/* The wrapper — not the `Skeleton` — carries `previewFrameRef`, because the
                shared `Skeleton` takes no `ref` (it is a plain function component, so one
                would be a type error and a silent no-op at runtime).

                Measuring here starts the width measurement one render EARLIER, on the
                skeleton's own column, and two things follow: this placeholder reserves
                `400 * previewScale` — the height the scaled email will actually occupy,
                rather than an unscaled 400 that would collapse on load — and the real
                frame mounts with `frameW` already correct. The layout effect lists
                `loadingList` in its deps for exactly this handover.

                The transparent 1px border is not decoration: the real frame has a 1px
                divider border, and `clientWidth` excludes it. Without a matching border
                here the skeleton would measure 2px wider than the frame it stands in for,
                and the scale would be computed against a width that does not exist. */}
            <Box
              ref={previewFrameRef}
              sx={{ boxSizing: 'border-box', border: '1px solid transparent' }}
            >
              <Skeleton variant="rounded" width="100%" height={Math.round(400 * previewScale)} />
            </Box>
          </Box>

          {panelOpen && (
            <Stack spacing={4} sx={{ width: PROPS_W, flexShrink: 0 }}>
              {/* THREE cards now, not two (VRG-560 second pass): Company, SMS, Props.
                  A skeleton left at two would grow a third card on load and push nothing
                  — the rail is its own scroll region — but it would still under-report
                  the panel by ~120px against the column beside it, which is the same
                  §19 parity failure in the other direction as the deleted band.

                  SMS drops from 232 to 116: the "Company name" field and the "White
                  label" row that made up the first ~116px of that card have moved into
                  Company, so what is left is the 72px body region plus the card's title
                  and padding. */}
              <Skeleton variant="rounded" width="100%" height={BRAND_CONTEXT_CARD_H} />
              <Skeleton variant="rounded" width="100%" height={116} />
              <Skeleton variant="rounded" width="100%" height={360} />
            </Stack>
          )}
        </Stack>
      </Box>
    )
  }

  if (!templates.length) {
    return (
      <Box sx={{ border: theme => `1px dashed ${theme.palette.divider}`, borderRadius: 2, p: 3 }}>
        <Typography variant="body2" color="text.secondary">
          No email templates registered in <code>@repo/emails</code> yet.
        </Typography>
      </Box>
    )
  }

  const propLeaves = flattenProps(props)

  return (
    <Box>
      {/* ── No sticky band here any more, and no `pt` on this row (VRG-560) ──────
          The band that sat here carried the App/Platform tabs plus the Dark and
          Hide-panel controls. The tabs moved into the LEFT COLUMN, because they filter
          the index that column holds; the two view controls moved into the PREVIEW
          COLUMN, because they act on the email. What was left was an empty sticky
          element, so it is gone — and with it the second published offset that every
          `top` further down had to be summed against (see `LABS_CONTENT_TOP`).

          ⚠️ Nothing here may take a `pt` or `mt`. The left column pins directly below
          the page header, so a gap above this row would put the column's REST position
          below its PINNED position, and it would travel that distance on the first
          scroll — the identical flow-spacing failure that made the old band slide 24px
          (measured y=182 -> y=158). The gap above this row is the header block's own
          `pb`, `LABS_HEADER_GAP`, which is inside the measured block. */}
      {/* A plain flex row with per-child `ml`, NOT `<Stack spacing>` — and that is forced,
          not stylistic. Stack writes its gap as `& > :not(style) ~ :not(style) { margin-left }`
          on the PARENT, which outranks an `sx` class on a child, so the panel's margin could
          not be animated to 0 while the Stack held that rule. The geometry is identical: Stack
          margins children 2 and 3, which is exactly what these two `ml`s do. (The skeleton
          branch above keeps its `Stack` — nothing there animates, and it reads the same
          `COL_GAP`, so the two cannot disagree.) */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
        <LabsSideColumn
          scope={{ options: SCOPE_OPTIONS, value: activeGroup, onChange: selectGroup }}
          search={{ value: search, onChange: setSearch, placeholder: 'Search notifications' }}
          groups={grouped.map(({ category, items }) => ({
            category,
            items: items.map(t => ({ label: t.name, value: t.id })),
          }))}
          activeValue={selectedId}
          onSelect={selectTemplate}
          emptyText="No notifications match your search."
        />

        {/* ── Preview column: the email, then its SMS equivalent ── */}
        <Box sx={{ flex: 1, minWidth: 0, ml: COL_GAP }}>
        {selected ? (
          <Stack spacing={6}>
            <Box>
              {/* ── Caption row: EMAIL, and the Prev/Next walk ──────────────────
                  Prev/Next exists because the actual job on this surface is reviewing all
                  47 templates in order, and doing that through the TOC alone means a trip
                  back to the column between every one. It walks `ordered` — the TOC's own
                  flattened order — so "next" is always the row visibly below the current
                  one, including under an active search.

                  Disabled with a tooltip rather than hidden at the ends (§46): the pair is
                  a fixed control, and a button that vanishes at the last template moves
                  the row's layout underneath the cursor mid-review. */}
              {/* ── The preview column's control row ────────────────────────────
                  Left: what this is, and whether it is being shown at true size. Right:
                  everything that acts on it — the walk through the index, the light/dark
                  rendering, and the panel toggle that is the only route back to 1:1.

                  The last two arrived here when the sticky band was deleted (VRG-560),
                  and this is where they belong rather than in the left column: the
                  appearance toggle re-renders the ARTIFACT below, and "Hide panel" sizes
                  it. Putting them beside the index would have repeated, one level down,
                  the mistake of keeping a control away from the thing it changes.

                  ⚠️ This row DOES wrap at rest, and that is measured rather than assumed:
                  the caption group is 128.8px, the control group is 394.5px, and with the
                  16px `columnGap` that is **539.3px against a 487px column**. It wraps to
                  two lines (caption at y=178, controls at y=210.9, row height 80.9).

                  No column gap saves it — at the previous 20px gap the column is 511, and
                  even at the original 12px it is 527, both under 539. The row simply
                  carries more than fits once the band's two controls joined it. The wrap
                  is therefore the DESIGN here, not a fallback, and it is adaptive in the
                  direction that matters: with the panel hidden the column is 831 and all
                  four groups sit on one line.

                  The alternative considered and rejected was making "Hide panel"
                  icon-only, which recovers ~78px and would fit. That reverses a decision
                  this file already argues for below — a labelled toggle states what the
                  click does, where an icon plus a tooltip does not — so the label stays
                  and the row wraps.

                  `useFlexGap` + `flexWrap` are what make that wrap graceful. Unwrapped,
                  an over-long row here does not compress, it OVERFLOWS: the title/send-test
                  row below records exactly that failure, where "Send test" rendered on top
                  of the props rail. `minHeight` is the 48px toggle track, so the row does
                  not change height as the scale readout appears and disappears. */}
              <Stack
                direction="row"
                useFlexGap
                flexWrap="wrap"
                rowGap={3}
                columnGap={4}
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 1.5, minHeight: 48 }}
              >
                <Stack direction="row" alignItems="baseline" spacing={2}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    EMAIL
                  </Typography>
                  {/* Only when it is NOT 1:1. A preview that has been shrunk is a
                      different artifact from one that has not, and the reader has to be
                      able to tell without measuring — but a permanent "100%" beside every
                      template is noise, so the label appears exactly when it carries
                      information.

                      Load-bearing since VRG-560 removed the "Fit width" switch: that
                      toggle used to be a second, standing indication that the preview was
                      scaled. This readout is now the ONLY thing on screen that says so. */}
                  {previewScale < 1 && (
                    <Typography variant="caption" color="text.secondary">
                      scaled to {scalePct}%
                    </Typography>
                  )}
                </Stack>
                <Stack direction="row" alignItems="center" spacing={3}>
                  <Stack direction="row" alignItems="center" spacing={2}>
                  <Tooltip
                    title={
                      orderIndex < 0
                        ? 'The selected notification is not in the current filter.'
                        : prevTemplate
                          ? prevTemplate.name
                          : 'First notification in the list.'
                    }
                  >
                    {/* A disabled MUI button swallows pointer events, so the Tooltip
                        anchors to a wrapper — same construction as `SideNav`'s. */}
                    <span>
                      <IconButton
                        size="small"
                        aria-label="Previous notification"
                        disabled={!prevTemplate}
                        onClick={() => prevTemplate && selectInternal(prevTemplate)}
                      >
                        <ChevronLeftIcon size={18} strokeWidth={1} />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    {orderIndex >= 0 ? `${orderIndex + 1} of ${ordered.length}` : `— of ${ordered.length}`}
                  </Typography>
                  <Tooltip
                    title={
                      orderIndex < 0
                        ? 'The selected notification is not in the current filter.'
                        : nextTemplate
                          ? nextTemplate.name
                          : 'Last notification in the list.'
                    }
                  >
                    <span>
                      <IconButton
                        size="small"
                        aria-label="Next notification"
                        disabled={!nextTemplate}
                        onClick={() => nextTemplate && selectInternal(nextTemplate)}
                      >
                        <ChevronRightIcon size={18} strokeWidth={1} />
                      </IconButton>
                    </span>
                  </Tooltip>
                  </Stack>

                  {/* Renders the EMAIL in its own light or dark treatment — this is not the
                      platform's colour mode, it is the `mode` the server composes the
                      template with, which is why it drives local state rather than
                      `useColorMode`.

                      Was a bare `Switch` labelled "Dark" in the deleted band. A switch is a
                      boolean affordance and this is a CHOICE BETWEEN TWO RENDERINGS, neither
                      of which is the "off" one — and the product already has a control that
                      says exactly that, on Company General's logo section and in Studio's
                      email preview. `AppearanceToggle` is that control, extracted (§36); see
                      its docblock for the two call sites still carrying their own copy. */}
                  <AppearanceToggle value={dark ? 'dark' : 'light'} onChange={next => setDark(next === 'dark')} />

                  {/* One control, labelled in both states. A single toggle whose label says
                      what the click does beats a "Props" button plus a close "×" on the rail:
                      two affordances for one job, and neither of them stating the state.
                      `PanelRightClose/OpenIcon` is the house glyph for a right-docked panel
                      (AskPanel, AnalyticsCustomize).

                      "Panel", not "props": the rail carries the SMS card as well, so a label
                      naming only one of its two cards would describe less than the button
                      does.

                      It sits in this row rather than the deleted band because it is a control
                      over the PREVIEW — it is the lever that takes the email back to a true
                      100% (see the geometry table), which is a statement about the artifact
                      in this column, not about the index in the left one. */}
                  {/* `size="large"` (49) because this row's band is set by the 48px
                      `ToggleButton` TRACK beside it, not by a page header. At the default
                      medium (40) the button sat 8px shorter than the two toggles it shares
                      a row with — measured 40 against 48, tops at y=182 vs y=178 — which
                      reads as a smaller, lesser control rather than a peer.

                      49 rather than a hand-set 48, and the 1px is deliberate: 49 is an
                      established §29 band (fields, table toolbar) and `large` is the token
                      that carries it, whereas 48 is `ToggleButton`'s own hardcoded track
                      height and is a band nothing else in the app answers to. §29's warning
                      is against introducing a FOURTH height, so this takes the nearest real
                      token instead of an `sx={{ height: 48 }}` magic number that would drift
                      the moment either control is retuned. At 1px the difference is not
                      resolvable on screen; measured tops now agree to within that pixel.

                      NOT `size="small"` — §60 forbids it outright, and it is the direction
                      that caused the complaint. */}
                  <Button
                    variant="secondary"
                    size="large"
                    onClick={togglePanel}
                    startIcon={
                      panelOpen ? (
                        <PanelRightCloseIcon size={18} strokeWidth={1} />
                      ) : (
                        <PanelRightOpenIcon size={18} strokeWidth={1} />
                      )
                    }
                  >
                    {panelOpen ? 'Hide panel' : 'Show panel'}
                  </Button>
                </Stack>
              </Stack>
              {/* WRAPS rather than overflowing (VRG-560). The preview column is now
                  flexible — 423px measured with the props rail open at 1280 — and a
                  fixed 260px field plus its button do not fit beside the title there.
                  Unwrapped this row did not compress, it OVERFLOWED: the "Send test"
                  button rendered on top of the props rail. `useFlexGap` is required for
                  `rowGap` to apply to a wrapping MUI Stack. */}
              <Stack
                direction="row"
                useFlexGap
                flexWrap="wrap"
                rowGap={3}
                columnGap={4}
                justifyContent="space-between"
                alignItems="flex-end"
                sx={{ mb: 3 }}
              >
                {/* ── Subject only. The `Title:` line above it is deleted (VRG-560) ──
                    It printed the registry's catalog NAME, which is verbatim the row
                    already selected and highlighted in the index two columns to the
                    left — the same word twice on one screen, in the largest type on
                    the surface, formatted exactly like the field beneath it.

                    That formatting is what made it worse than redundant. `Title:` and
                    `Subject:` read as two properties of the email, so the eye takes the
                    first, larger one as the more important — and a reader checking what
                    lands in an inbox was being shown an internal label first. Deleting
                    it promotes the SUBJECT, which is the only one of the two a recipient
                    ever sees. The status chip moves onto that row with it. */}
                <Stack direction="row" alignItems="center" spacing={2} sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 20, fontWeight: 700 }}>
                    <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                      Subject:{' '}
                    </Box>
                    {selected.subject}
                  </Typography>
                  <StatusChip statuses={TEMPLATE_STATUSES} status={selected.status} size="small" />
                </Stack>
                <Stack direction="row" spacing={3} alignItems="flex-end" sx={{ flexShrink: 0 }}>
                  <Box sx={{ width: 260 }}>
                    <Input
                      label="Send test to"
                      value={to}
                      onValueChange={setTo}
                      placeholder={profile?.email || 'email@example.com'}
                    />
                  </Box>
                  <Button variant="primary" size="large" loading={sending} onClick={sendTest} disabled={!to.trim()}>
                    Send test
                  </Button>
                </Stack>
              </Stack>
              {/* ── The preview frame ──────────────────────────────────────────────
                  History, because the current shape only makes sense against it. The
                  email card is `width: 600px; max-width: 100%`, and that `100%` is of the
                  IFRAME's body, which the 600px table then widens — so a narrow column
                  did not shrink the email, it CLIPPED it, with nothing on screen saying
                  the right edge was gone. That was replaced by `minWidth: 600` plus a
                  horizontal scroll, which at least made the loss visible.

                  Visible loss is still loss. Measured at 1280x800 with the sidebar
                  EXPANDED and the panel open, the column is 383px — the email was two
                  thirds off-screen on the arrangement most people actually have. So the
                  frame SCALES the whole card into the column instead, always.

                  `overflowX: 'auto'` is kept as a FAILSAFE, not as a feature. With the
                  scale clamped to ≤1 the wrapper is `EMAIL_W * previewScale ≤ frameW` and
                  nothing can overflow — except on the single render before the layout
                  effect measures, where `frameW` is 0, `previewScale` falls back to 1 and
                  a 600px card briefly sits in a narrower frame. `hidden` there would clip
                  it silently, which is the original bug; `auto` degrades to a scrollbar
                  for one frame instead. Do not "tidy" it away. */}
              <Box
                ref={previewFrameRef}
                sx={{
                  position: 'relative',
                  border: theme => `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                  overflowX: 'auto',
                  overflowY: 'auto',
                  // The email's own page tone, read off the rendered email (`previewPageBg`).
                  // NOT `dark ? '#0F0F11' : '#ffffff'` — those matched neither the email's page
                  // background nor its card, so the email's 24px outer band was fenced by a
                  // colour step at both edges and read as a stripe across the preview.
                  bgcolor: previewPageBg ?? 'transparent',
                  minHeight: 200,
                  maxHeight: 900,
                }}
              >
                {loadingPreview && (
                  <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1 }}>
                    <CircularProgress size={20} />
                  </Box>
                )}
                {/* The scale wrapper carries the SCALED box, and it is not optional:
                    `transform` does not affect layout, so without it the frame would keep
                    reserving the iframe's full 600 x previewHeight and leave dead space to
                    the right of and below a shrunk email — which is the "no layout gap
                    left behind" half of the fix. `mx: 'auto'` centres it when the column
                    is wider than the card, preserving the centred look the old
                    `width: 100%` iframe produced at 1:1. */}
                <Box
                  sx={{
                    width: EMAIL_W * previewScale,
                    height: previewHeight * previewScale,
                    mx: 'auto',
                    // The SCALED BOX moves on the panel's clock (`PANEL_SLIDE`). This is the
                    // layout half of the email's resize — the transform below is only paint,
                    // so without this the reserved box would snap to its new size while the
                    // painted email eased into it, and everything below the frame would jump.
                    // `height` matters as much as `width`: at 600x~1400 a 19-point scale
                    // change is ~270px of page height.
                    transition: panelAnimating ? `width ${PANEL_SLIDE}, height ${PANEL_SLIDE}` : 'none',
                    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                  }}
                >
                  {/* The iframe is FIXED at the card's own 600px and is scaled, never
                      resized — see `EMAIL_W`. Resizing it would let the email reflow, and
                      a reflowed email is not the artifact under review.
                      `transformOrigin: 'top left'` matches the wrapper's origin, so the
                      scaled box and the painted pixels start at the same point.

                      ⚠️ `maxWidth: 'none'` is LOAD-BEARING and was found by measuring, not
                      by reading. An ambient `max-width: 100%` applies to iframes here, and
                      it silently beat the 600: the frame laid out at 381px (the column's
                      width) while the email TABLE inside stayed 600 and overflowed its own
                      body — measured `iframe.offsetWidth 381`, `body.scrollWidth 600`.
                      So the card was still being clipped, only now clipped AND scaled,
                      which looks exactly like a working fit-to-width until you compare it
                      against the right edge of the email. Removing the cap is what makes
                      the iframe a true 600px coordinate space for the transform to act on. */}
                  <Box
                    component="iframe"
                    ref={previewIframeRef}
                    title="Email preview"
                    srcDoc={previewHtml}
                    onLoad={handlePreviewLoad}
                    sx={{
                      width: EMAIL_W,
                      maxWidth: 'none',
                      height: previewHeight,
                      border: 'none',
                      display: 'block',
                      // ── The black flash when switching templates ──────────────────
                      //
                      // `setPreviewHtml` runs in the fetch `.then()`; `setPreviewHeight`
                      // runs only in `handlePreviewLoad`, on the iframe's `load`. So for
                      // one commit the NEW, shorter document sits inside the OLD, taller
                      // box. Content heights span 439-993 across the corpus, so the
                      // uncovered strip reaches ~460px. Neither <html> nor <body> carries
                      // a background (deliberate — clients strip <body>), so that strip is
                      // painted by the UA canvas.
                      //
                      // `colorScheme: 'light'` is what stops it being BLACK, and it is not
                      // interchangeable with painting a colour underneath. Measured in the
                      // pane with four probe iframes: a document whose canvas resolves DARK
                      // paints an OPAQUE near-black over the iframe element's own
                      // background — a magenta `background-color` on the element was
                      // completely hidden. Pinning the element's `color-scheme` to light
                      // makes the canvas transparent again, at which point `backgroundColor`
                      // below (and the frame behind it) shows through. The email declares
                      // `color-scheme: light dark`, so it otherwise adopts the EMBEDDER's
                      // scheme — this pane prefers dark — which is why the strip is black
                      // today even though `matchMedia` inside the iframe reports light.
                      //
                      // Forcing light costs the preview nothing, and that is verified rather
                      // than assumed: the Labs render emits the dark rules UNCONDITIONALLY
                      // (measured — the served dark preview contains no
                      // `prefers-color-scheme` block at all; `mode` picks the token set
                      // server-side and the colours arrive inline). So no part of what is on
                      // screen depends on the iframe's used scheme. `color-scheme` reaches
                      // only UA-painted surfaces — the canvas, form controls, scrollbars —
                      // and this document is a table with none of them.
                      //
                      // Note this fix has NO timing dependency: the strip paints the email's
                      // own page tone whether or not `load` has fired, so there is no window
                      // to lose a race in. A height-reservation or min-height approach would
                      // still have to guess the new height before measuring it.
                      colorScheme: 'light',
                      // The email's own page tone, read off the rendered `.v-bg` — the same
                      // value the frame uses, from the same state, so the two cannot drift.
                      // See `previewPageBg`; this is deliberately not a retyped hex.
                      backgroundColor: previewPageBg ?? 'transparent',
                      transform: previewScale === 1 ? 'none' : `scale(${previewScale})`,
                      transformOrigin: 'top left',
                      // The PAINT half of the same move, on the same clock as the box above
                      // and as the rail itself. `none` at exactly 1:1 is kept deliberately —
                      // an identity `scale(1)` still rasterises through the transform path and
                      // costs this surface the crisp true-size render that "Hide panel" exists
                      // to reach. Interpolating `none` <-> `scale(n)` is well-defined: `none`
                      // is the identity matrix for transition purposes, so the 100% <-> 81%
                      // move animates like any other.
                      transition: panelAnimating ? `transform ${PANEL_SLIDE}` : 'none',
                      '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                    }}
                  />
                </Box>
              </Box>
            </Box>

            {/* SMS moved OUT of this column (VRG-560) — it now sits in the right panel.
                Below the email it was below the fold for any template taller than the
                viewport, and it was making this column hold two different widths. */}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Select a notification on the left to preview it.
          </Typography>
        )}
        </Box>

        {/* ── Right panel: Company, then SMS, then Props ───────────────────────
            Three bordered `Card`s, in that order. Each carries a TITLE rather than a
            `text.secondary` caption: the old props panel was below the fold AND
            unlabelled, so moving it without labelling it would have fixed one of the two
            reasons nobody found it, and the same argument applies to the other two.

            **Company is FIRST because it scopes both cards under it** (VRG-560 second
            pass). It decides which brand's marks the email above renders with, which
            name and link the SMS body composes from, and which catalogue the Props
            card's sample picker offers. Putting it below either would ask the reader to
            scroll past an output to reach the input that produced it.

            ── The collapse ──────────────────────────────────────────────────────
            Always MOUNTED, and animated to zero width, because `{panelOpen && …}` is what
            produced the flash: an unmounted child reflows the row in a single frame and there
            is nothing left to transition.

            `width` and `margin-left` both animate, and both are required — the gap is a real
            32px of layout, so collapsing only the width would leave the rail's gap sitting
            there as a permanent dead strip on the right.

            `visibility` rides in the same declaration purely to STAGE, exactly as `AskPanel`
            does: it flips to `visible` immediately when opening and only at the END of the
            close (`transition-delay`), so a collapsed rail is not a focus trap of invisible
            inputs and is not read out by a screen reader. A `0s` duration has nothing to
            interpolate, so it never fights the widths.

            `overflow: hidden` with the content held at a fixed `PROPS_W` inside means the
            cards are CLIPPED as the rail narrows rather than reflowing through a series of
            ever-narrower layouts — a 312px card re-wrapping its way down to 0 is both ugly
            and, at 47 templates' worth of prop inputs, not cheap. */}
        <Box
          onTransitionEnd={e => {
            // Reconcile the predicted scale against the real thing, once, at the end (see
            // `togglePanel`). Guarded on both the property and the target: this element's
            // subtree contains inputs and cards with their own transitions, and `opacity`
            // and `margin-left` land at the same moment `width` does.
            if (e.propertyName !== 'width' || e.target !== e.currentTarget) return
            panelAnimatingRef.current = false
            const el = previewFrameRef.current
            if (el) setFrameW(el.clientWidth)
          }}
          sx={{
            width: panelOpen ? PROPS_W : 0,
            ml: panelOpen ? COL_GAP : 0,
            flexShrink: 0,
            overflow: 'hidden',
            opacity: panelOpen ? 1 : 0,
            visibility: panelOpen ? 'visible' : 'hidden',
            transition: `width ${PANEL_SLIDE}, margin-left ${PANEL_SLIDE}, opacity ${PANEL_SLIDE}, visibility 0s linear ${panelOpen ? '0s' : `${ASK_PANEL_SLIDE_DURATION_MS}ms`}`,
            // The honest escape hatch: someone who has asked for less motion gets the old
            // instant swap, which was never WRONG — only abrupt. `togglePanel`'s predicted
            // scale is exact, so the email lands correctly here with no transition to ride.
            '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
          }}
        >
          <Stack spacing={4} sx={{ width: PROPS_W }}>
            <BrandContextCard
              companies={companies}
              value={company}
              onChange={setCompany}
              context={brandContext}
              loading={loadingContext}
              // Labs is Vera STAFF previewing SOMEONE ELSE's brand, so the card's default
              // (this user's own Settings → Installation) would be a link that silently
              // changes the wrong company's settings. The internal company page is the
              // surface a staff member actually operates that brand from.
              installationHref={
                company ? ROUTES.INTERNAL_COMPANY(company.id) : ROUTES.INTERNAL_COMPANIES
              }
              // `showUpgradeCta` is deliberately NOT passed — see the card's own docblock.
              // An upgrade offer to a Vera employee, about a brand's plan, is nonsense that
              // looks like a control. It belongs on the brand-facing Studio Email tab.
            />

            {/* SMS: what this template composes as a text, for the company picked above.
                The two controls that used to sit at the top of this card (a free-text
                "Company name" and a "White label" switch) are gone — they are resolved
                from that company now, so what is left here is purely the OUTPUT. */}
            <Card title="SMS" spacing={4}>
              {/* ── The SMS body, and why it settles rather than flashes (VRG-560) ──
                  Prev/Next made switching templates the surface's main interaction, and
                  every switch produced a visible flash: the body was replaced by a 72px
                  skeleton and then by the next body, so a single click swapped the
                  panel's contents TWICE and changed its height twice with it.

                  VRG-512's bug here was the opposite one — a skeleton that never cleared
                  (the `!hasSms` short-circuit returned without reaching the `.finally`
                  that puts `loadingSms` down). That fix stands; this is the other end of
                  the same control.

                  Three things, and the first is the one that matters:

                  1. The skeleton shows only when there is no previous BODY to hold
                     (`!smsPreview?.body`). Once a body has been shown, an in-flight
                     request keeps it on screen instead of tearing it down — so a switch
                     is one content change, not two. Keyed on `.body` rather than on
                     `smsPreview` being set at all, because the other non-null state is
                     "No SMS equivalent", and holding THAT while loading a template that
                     does have one would leave a false statement on screen.
                  2. The held body is dimmed and transitioned, so it reads as settling
                     rather than as the new template's text. It is the previous
                     template's, and it must not be mistaken for the current one.
                  3. `SMS_BODY_MIN_H` floors the region, so the bubble, the skeleton and
                     the empty state share a height and the card cannot collapse between
                     them. Bodies of genuinely different length still change the height —
                     that is content, not a flash — but nothing snaps to zero. */}
              <Box
                sx={{
                  minHeight: SMS_BODY_MIN_H,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  transition: 'opacity 160ms ease',
                  opacity: loadingSms && smsPreview?.body ? 0.4 : 1,
                }}
              >
                {loadingSms && !smsPreview?.body ? (
                  <Skeleton variant="rounded" width="100%" height={SMS_BODY_MIN_H} />
                ) : smsPreview?.hasSms && smsPreview.body ? (
                  // Plain text, not HTML — SMS has no markup — rendered as a phone bubble
                  // so it reads as a text message rather than another email panel.
                  <Box
                    sx={{
                      px: 3,
                      py: 2,
                      borderRadius: 4,
                      // Rough iMessage-blue bubble — enough to read as "this is a text",
                      // not a design-system component (this page is internal tooling).
                      bgcolor: dark ? '#0A84FF' : '#0B93F6',
                      color: '#fff',
                    }}
                  >
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: 'inherit' }}>
                      {smsPreview.body}
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ border: theme => `1px dashed ${theme.palette.divider}`, borderRadius: 2, p: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      No SMS equivalent for this notification.
                    </Typography>
                  </Box>
                )}
              </Box>
            </Card>

            {/* Leaves only — `flattenProps` walks plain objects, so a nested prop like
                `scanDetails.tagId` is its own field rather than an `[object Object]`
                input. Stacked one per row: at 232px of inner content there is nowhere to
                put a second column. */}
            <Card title="Props" spacing={4}>
              {/* ── Fill the product props from a REAL product (VRG-560) ─────────
                  The registry's sample props are a Nike Air Max 90 with a `cdn.vera.inc`
                  image that 404s, so every product-bearing template previewed as the same
                  fictional shoe with a broken picture. Typing a real title into the box
                  below gets you the title and nothing else — not the SKU, not the image —
                  because they are three separate fields and nobody fills three.

                  So: pick one of the picked COMPANY's products and every product path
                  this template has is written at once. Which paths those are is read off
                  the template (`productFieldForPath`), never assumed, so a template using
                  a shape nobody has written yet gets no picker rather than a control that
                  writes into nothing.

                  Rendered only when there is a company AND this template has product
                  paths. §46's axis: this is RECORD STATE (a template with no product prop
                  has nothing for a product to fill), so it hides rather than sitting
                  disabled with a tooltip explaining a dead end. The resolving action — the
                  company picker — is one card up and always reachable, which is the
                  obligation hiding creates. */}
              {company && productPropPaths.length > 0 && (
                <ProductSelectField
                  label="Sample product"
                  products={(brandContext ? sampleProducts : []) as any}
                  value={sampleProduct as any}
                  onChange={applySampleProduct}
                />
              )}
              {!selected ? (
                <Typography variant="body2" color="text.secondary">
                  Select a notification to edit its props.
                </Typography>
              ) : propLeaves.length ? (
                propLeaves.map(({ path, value }) => (
                  <Input
                    key={path}
                    label={path}
                    value={String(value ?? '')}
                    onValueChange={v => setProp(path, v)}
                  />
                ))
              ) : (
                // Stated rather than left blank: an empty bordered panel reads as a
                // failed load, and several templates genuinely take no props.
                <Typography variant="body2" color="text.secondary">
                  This template takes no props.
                </Typography>
              )}
            </Card>
          </Stack>
        </Box>
      </Box>
    </Box>
  )
}
